/**
 * Division Grouping Utilities
 * Generic functions for grouping items by division number (1-N)
 * Used across all divisional chart info tables (D8, D12, D20, D24, D27, D30, D40, D60)
 */

/**
 * Initialize empty arrays for each division (1 to maxDivisions)
 * @param maxDivisions - Number of divisions (e.g., 8 for D8, 27 for Nakshatra)
 * @returns Record with keys 1 to maxDivisions, each with empty array
 */
export function initializeDivisionGroups<T>(maxDivisions: number): Record<number, T[]> {
  const groups: Record<number, T[]> = {};
  for (let i = 1; i <= maxDivisions; i++) {
    groups[i] = [];
  }
  return groups;
}

/**
 * Group items by a numeric division property
 * @param items - Array of items to group
 * @param getDivision - Function to extract division number from item
 * @param maxDivisions - Maximum number of divisions
 * @returns Record with items grouped by division (1 to maxDivisions)
 */
export function groupByDivision<T>(
  items: T[],
  getDivision: (item: T) => number,
  maxDivisions: number
): Record<number, T[]> {
  const groups = initializeDivisionGroups<T>(maxDivisions);

  items.forEach(item => {
    const division = getDivision(item);
    if (division >= 1 && division <= maxDivisions) {
      groups[division].push(item);
    }
  });

  return groups;
}

/**
 * Common division counts for Vedic astrology
 */
export const DIVISION_COUNTS = {
  ASHTAMSHA: 8,       // D8
  DWADASHAMSHA: 12,   // D12
  VIMSHAMSHA: 20,     // D20
  CHATURVIMSHAMSHA: 24, // D24
  NAKSHATRA: 27,      // D27
  TRIMSAMSHA: 30,     // D30
  KHAVEDAMSHA: 40,    // D40
  SHASHTYAMSHA: 60,   // D60
} as const;
